import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';

// Import Stripe configuration
import { stripePromise } from '../../config/stripe';

interface VPSPlan {
  id: string;
  name: string;
  description: string;
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
  bandwidth_gb: number;
  monthly_price: string;
  hourly_price: string;
  features: {
    ssd_storage: boolean;
    backup_included: boolean;
    monitoring_included: boolean;
    load_balancer: boolean;
    dedicated_ip: boolean;
    priority_support?: boolean;
  };
  is_active: boolean;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  cardVerified?: boolean;
  card_verified?: boolean;
}

// Authentication Modal Component
const AuthModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (user: User) => void;
}> = ({ isOpen, onClose, onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
      const fullUrl = `${apiUrl}${endpoint}`;

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.data.token);
        onAuthSuccess(data.data.user);
      } else {
        setError(data.error || 'Authentication failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl shadow-2xl p-8 w-full max-w-md transform transition-all duration-200 relative overflow-hidden">
        {/* Glossy overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent rounded-2xl"></div>
        
        {/* Content */}
        <div className="relative z-10">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <div className="inline-flex items-center px-4 py-2 mb-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
                <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
                <span className="text-sm font-semibold text-gray-700">{isLogin ? 'Secure Login' : 'Join 12and22'}</span>
              </div>
              <h2 className="text-3xl font-black text-gray-900 mb-2">
                {isLogin ? 'Welcome' : 'Create'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">{isLogin ? 'Back' : 'Account'}</span>
              </h2>
              <p className="text-gray-600 text-sm">
                {isLogin ? 'Sign in to deploy your VPS' : 'Join 12and22 to get started'}
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-xl hover:bg-white/50 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl"></div>
              <svg className="w-5 h-5 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 mb-6 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
              <div className="relative z-10 flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center mr-3 shadow-md relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
                  <svg className="w-4 h-4 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-red-700 text-sm font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
          {!isLogin && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white/90"
                    required
                  />
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white/90"
                    required
                  />
                </div>
              </div>
              <div className="relative">
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white/90"
                />
              </div>
            </>
          )}
          
          <div className="relative">
            <input
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white/90"
              required
            />
          </div>
          
          <div className="relative">
            <input
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all duration-200 bg-white/70 backdrop-blur-sm hover:bg-white/90"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-xl"></div>
            <span className="relative z-10">
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Please wait...
                </div>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </span>
          </button>
        </form>

          {/* Toggle Auth Mode */}
          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm mb-3">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
            </p>
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-primary-navy hover:text-orange-500 font-semibold text-sm underline transition-colors duration-200"
            >
              {isLogin ? "Sign up for free" : "Sign in instead"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment + Deploy form (production with Stripe).
// NEW global flow — same pattern as the hosting/email/domain checkout:
//  1) POST /api/vps-subscriptions/create-payment-intent (plan + cycle)
//  2) stripe.confirmCardPayment(clientSecret)  — 3-D Secure + India mandate
//  3) POST /api/vps-subscriptions/provision-after-payment  — Proxmox VM
// The legacy "verify card with $1 charge first" step has been retired.
const StripePaymentVerificationForm: React.FC<{
  onVerificationSuccess: () => void;
  onError: (error: string) => void;
  planId: string;
  billingCycle: 'monthly' | 'yearly';
  vpsName: string;
  osTemplate: string;
}> = ({ onVerificationSuccess, onError, planId, billingCycle, vpsName, osTemplate }) => {
  const [loading, setLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!stripe || !elements) {
        onError('Stripe not initialized');
        setLoading(false);
        return;
      }
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        onError('Card element not found');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        onError('Please log in again to continue.');
        setLoading(false);
        return;
      }
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001';

      // Step 1: Ask backend for a PaymentIntent for this plan + cycle.
      const piResp = await fetch(`${apiBase}/api/vps-subscriptions/create-payment-intent`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId, billingCycle }),
      });
      const piData = await piResp.json();
      if (!piData.success || !piData.data?.clientSecret) {
        onError(piData.error || 'Failed to start payment.');
        setLoading(false);
        return;
      }

      // Step 2: Confirm the PaymentIntent with the card. Stripe handles
      // 3-D Secure + Indian RBI e-mandate registration inline here.
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        piData.data.clientSecret,
        { payment_method: { card: cardElement } },
      );
      if (confirmError) {
        onError(confirmError.message || 'Payment failed.');
        setLoading(false);
        return;
      }
      if (paymentIntent?.status !== 'succeeded') {
        onError(`Payment did not complete (status: ${paymentIntent?.status}). Your card was not charged.`);
        setLoading(false);
        return;
      }

      // Step 3: Provision the VPS now that money is in.
      const provResp = await fetch(`${apiBase}/api/vps-subscriptions/provision-after-payment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id,
          planId,
          billingCycle,
          vpsName,
          osTemplate,
        }),
      });
      const provData = await provResp.json();
      if (provData.success) {
        onVerificationSuccess();
      } else {
        onError(provData.error || 'Payment succeeded but provisioning failed. Our team has been notified.');
      }
    } catch (error: any) {
      onError(error?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleVerification} className="space-y-6">
      <div className="border border-gray-300 rounded-xl p-4 bg-gray-50">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#374151',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                '::placeholder': {
                  color: '#9CA3AF',
                },
              },
            },
          }}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Processing payment & provisioning...
          </div>
        ) : (
          'Pay & Deploy VPS'
        )}
      </button>
    </form>
  );
};

// Development Bypass Payment Form (no Stripe hooks)
const DevelopmentPaymentVerificationForm: React.FC<{
  onVerificationSuccess: () => void;
  onError: (error: string) => void;
}> = ({ onVerificationSuccess, onError }) => {
  const [loading, setLoading] = useState(false);

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Card verified through Stripe integration
      const paymentMethodId = 'pm_dev_bypass_' + Date.now();

      // Verify card with backend
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/payments/verify-card`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paymentMethodId })
      });

      const data = await response.json();

      if (data.success) {
        onVerificationSuccess();
      } else {
        onError(data.error || 'Card verification failed');
      }
    } catch (error) {
      onError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleVerification} className="space-y-6">
      <div className="border border-orange-300 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-green-700 text-sm font-semibold">
              Card Verified
            </p>
            <p className="text-green-600 text-xs mt-1">
              Your payment method has been verified successfully
            </p>
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
            Verifying...
          </div>
        ) : (
          'Bypass Verification (Dev)'
        )}
      </button>
    </form>
  );
};

// Payment Verification Modal Component
const PaymentVerificationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onVerificationSuccess: () => void;
  planId: string;
  billingCycle: 'monthly' | 'yearly';
  vpsName: string;
  osTemplate: string;
  amount: number;
}> = ({ isOpen, onClose, onVerificationSuccess, planId, billingCycle, vpsName, osTemplate, amount }) => {
  const [error, setError] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md transform transition-all duration-200">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Pay & Deploy VPS</h2>
            <p className="text-gray-600 text-sm mt-1">
              ${amount.toFixed(2)} • {billingCycle} billing
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Info Section */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-blue-900 mb-1">First period charge</h3>
                <p className="text-blue-700 text-sm">
                  ${amount.toFixed(2)} will be charged now for the first {billingCycle === 'yearly' ? 'year' : 'month'}. Your VPS provisions automatically after payment succeeds. Next charge is at renewal.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-700 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Payment Form */}
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <StripePaymentVerificationForm
              onVerificationSuccess={onVerificationSuccess}
              onError={setError}
              planId={planId}
              billingCycle={billingCycle}
              vpsName={vpsName}
              osTemplate={osTemplate}
            />
          </Elements>
        ) : (
          <div className="text-center text-gray-600">
            Loading payment system...
          </div>
        )}

        {/* Security Note */}
        <div className="mt-6 p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <p className="text-gray-600 text-xs">
              Your payment information is secured with industry-standard encryption
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Password strength checker
const getPasswordStrength = (password: string) => {
  let score = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    numbers: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  
  score = Object.values(checks).filter(Boolean).length;
  
  if (score < 2) return { strength: 'weak', color: 'red', text: 'Weak' };
  if (score < 4) return { strength: 'medium', color: 'yellow', text: 'Medium' };
  return { strength: 'strong', color: 'green', text: 'Strong' };
};

// Main Streamlined VPS Checkout Component
const StreamlinedVPSCheckout: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading, refreshUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<VPSPlan | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // userHasVerifiedCard removed — the new global flow does payment + provision
  // in one inline step, no separate card-verification precondition.
  
  // VPS Configuration
  const [vpsName, setVpsName] = useState('');
  const [osTemplate, setOsTemplate] = useState('ubuntu:22.04');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Load selected plan from sessionStorage
    const savedPlan = sessionStorage.getItem('selectedVPSPlan');
    if (savedPlan) {
      setSelectedPlan(JSON.parse(savedPlan));
    } else {
      // No plan selected, redirect back to VPS page
      navigate('/vps#packages');
      return;
    }

    // Only check auth status after a short delay to allow AuthContext to initialize
    setTimeout(() => {
      checkAuthAndPaymentStatus();
    }, 300);
  }, [navigate]);

  // Watch for changes in authUser and loading state
  useEffect(() => {
    // Only run this check if auth loading is complete
    if (!authLoading) {
      console.log('🔄 AuthContext loading complete, re-checking status...');
      checkAuthAndPaymentStatus();
    }
  }, [authUser, authLoading]);


  const checkAuthAndPaymentStatus = async () => {
    console.log('🔍 Checking auth status - authLoading:', authLoading, 'authUser:', !!authUser);
    
    // Wait for auth loading to complete
    if (authLoading) {
      console.log('⏳ Auth still loading, waiting...');
      return;
    }
    
    const token = localStorage.getItem('token');
    console.log('🔑 Token exists:', !!token);
    
    // If we have a token but no authUser yet, wait a bit more for the AuthContext to load
    // But limit retries to prevent infinite loop
    if (token && !authUser) {
      const retryCount = parseInt(sessionStorage.getItem('authRetryCount') || '0');
      if (retryCount < 3) {
        console.log('⏳ Token exists but authUser not loaded yet, retry', retryCount + 1, 'of 3...');
        sessionStorage.setItem('authRetryCount', (retryCount + 1).toString());
        // Give AuthContext more time to initialize
        setTimeout(() => {
          checkAuthAndPaymentStatus();
        }, 500);
        return;
      } else {
        console.log('❌ Auth loading failed after 3 retries, treating as unauthenticated');
        sessionStorage.removeItem('authRetryCount');
        // Token might be invalid, clear it and show auth modal
        localStorage.removeItem('token');
        setShowAuthModal(true);
        return;
      }
    }
    
    // Clear retry count on successful auth
    if (authUser) {
      sessionStorage.removeItem('authRetryCount');
    }
    
    if (!token || !authUser) {
      // User not authenticated, show auth modal
      console.log('❌ User not authenticated - token:', !!token, 'authUser:', !!authUser);
      setShowAuthModal(true);
      return;
    }
    
    console.log('✅ User is authenticated:', authUser.email);

    // NEW global flow: there is no separate "card verification" step. The
    // user clicks Deploy on the page, which opens the pay-and-deploy modal
    // and runs PaymentIntent → confirm → provision in one go. We do NOT
    // auto-open that modal here — just confirm auth is good.
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Auth OK — let user fill in VPS name etc. and click Deploy.
        setShowAuthModal(false);
        setShowPaymentModal(false);
      } else {
        console.log('❌ Profile fetch failed, token invalid');
        // Token invalid, show auth modal
        localStorage.removeItem('token');
        setShowAuthModal(true);
        setShowPaymentModal(false);
      }
    } catch (error) {
      console.error('Error checking user status:', error);
      setShowAuthModal(true);
      setShowPaymentModal(false);
    }
  };

  const handleAuthSuccess = async (userData: User) => {
    console.log('🎉 Auth success, user data:', userData);
    setShowAuthModal(false);
    
    // Refresh the AuthContext to get the latest user data
    try {
      await refreshUser();
      console.log('✅ AuthContext refreshed');
      
      // Wait a bit more for the AuthContext to update
      setTimeout(() => {
        console.log('🔄 Re-checking auth and payment status after AuthContext refresh...');
        checkAuthAndPaymentStatus();
      }, 500);
      
    } catch (error) {
      console.error('❌ Error refreshing user:', error);
      // Fallback: just check the status anyway
      setTimeout(() => {
        checkAuthAndPaymentStatus();
      }, 200);
    }
    
  };

  // Called after the modal has run: PaymentIntent → confirmCardPayment →
  // provision-after-payment all succeeded. Money is taken, VM is provisioning.
  const handlePaymentAndProvisionSuccess = async () => {
    console.log('🎉 Payment + VPS provisioning kicked off successfully!');
    setShowPaymentModal(false);
    sessionStorage.removeItem('selectedVPSPlan');
    navigate('/customer/vps');
  };

  // Deploy button handler — validates basic fields and opens the
  // pay-and-deploy modal which runs the full PaymentIntent → confirm →
  // provision flow.
  const handleVPSCreation = () => {
    if (!selectedPlan || !authUser) {
      setError('Please sign in and pick a plan first.');
      return;
    }
    if (!vpsName.trim()) {
      setError('Please enter a VPS name.');
      return;
    }
    setError('');
    setShowPaymentModal(true);
  };

  if (!selectedPlan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const monthlyPrice = parseFloat(selectedPlan.monthly_price);
  const totalPrice = billingCycle === 'yearly' ? monthlyPrice * 12 * 0.9 : monthlyPrice;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Deploy Your VPS</h1>
          <p className="text-gray-600">Configure and deploy your {selectedPlan.name} VPS instance</p>
        </div>

        {error && (
          <div className="max-w-2xl mx-auto mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Configuration Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-6">VPS Configuration</h2>
              
              <div className="space-y-6">
                {/* VPS Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    VPS Instance Name *
                  </label>
                  <input
                    type="text"
                    value={vpsName}
                    onChange={(e) => setVpsName(e.target.value)}
                    placeholder="my-vps-server"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Choose a memorable name for your VPS instance
                  </p>
                </div>

                {/* Operating System */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Operating System
                  </label>
                  <select
                    value={osTemplate}
                    onChange={(e) => setOsTemplate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="ubuntu:22.04">Ubuntu 22.04 LTS</option>
                    <option value="ubuntu:20.04">Ubuntu 20.04 LTS</option>
                    <option value="debian:11">Debian 11</option>
                    <option value="centos:8">CentOS 8</option>
                    <option value="alpine:3.18">Alpine Linux 3.18</option>
                  </select>
                </div>

                {/* Default SSH Password Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">🔐 Default SSH Password</h3>
                  <p className="text-sm text-blue-800 mb-2">
                    All VPS instances are created with a standard password:
                  </p>
                  <div className="bg-white border border-blue-300 rounded px-3 py-2 font-mono text-sm text-gray-800 mb-3 flex items-center justify-between">
                    <span>TempPassw0rd!A</span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText('TempPassw0rd!A');
                        // You could add a toast notification here
                      }}
                      className="text-blue-600 hover:text-blue-800 text-xs"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                    <p className="text-xs text-yellow-800">
                      ⚠️ <strong>Important:</strong> Please change this password immediately after your first login for security.
                    </p>
                  </div>
                </div>

                {/* Billing Cycle */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Billing Cycle
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setBillingCycle('monthly')}
                      className={`p-3 border rounded-lg text-center ${
                        billingCycle === 'monthly'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-semibold">Monthly</div>
                      <div className="text-sm text-gray-600">${monthlyPrice.toFixed(2)}/month</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setBillingCycle('yearly')}
                      className={`p-3 border rounded-lg text-center ${
                        billingCycle === 'yearly'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-semibold">Yearly</div>
                      <div className="text-sm text-gray-600">${(monthlyPrice * 12 * 0.9).toFixed(2)}/year</div>
                      <div className="text-xs text-green-600">Save 10%</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-8">
              <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
              
              {/* Plan Details */}
              <div className="border-b border-gray-200 pb-4 mb-4">
                <h4 className="font-medium text-gray-900">{selectedPlan.name}</h4>
                <div className="text-sm text-gray-600 mt-2 space-y-1">
                  <div>💾 {selectedPlan.cpu_cores} vCPU</div>
                  <div>🧠 {selectedPlan.memory_mb}MB RAM</div>
                  <div>💽 {selectedPlan.disk_gb}GB SSD</div>
                  <div>📡 {selectedPlan.bandwidth_gb}GB Transfer</div>
                </div>
              </div>

              {/* Pricing */}
              <div className="space-y-2 mb-6">
                <div className="flex justify-between">
                  <span>Plan ({billingCycle}):</span>
                  <span>${billingCycle === 'monthly' ? monthlyPrice.toFixed(2) : (monthlyPrice * 12).toFixed(2)}</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div className="flex justify-between text-green-600">
                    <span>Yearly Discount (10%):</span>
                    <span>-${(monthlyPrice * 12 * 0.1).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>${totalPrice.toFixed(2)}/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                </div>
              </div>

              {/* Deploy Button */}
              <button
                onClick={handleVPSCreation}
                disabled={loading || !authUser || !vpsName.trim()}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                {loading ? 'Creating VPS...' : `Deploy VPS - $${totalPrice.toFixed(2)}`}
              </button>

              {/* Status Indicators */}
              <div className="mt-4 space-y-2 text-sm">
                <div className={`flex items-center ${authUser ? 'text-green-600' : 'text-gray-500'}`}>
                  <span className="mr-2">{authUser ? '✓' : '○'}</span>
                  Authentication {authUser ? 'Complete' : 'Required'}
                </div>
                <div className="flex items-center text-green-600">
                  <span className="mr-2">✓</span>
                  Pay & Deploy (card collected on next step)
                </div>
                <div className="flex items-center text-green-600">
                  <span className="mr-2">✓</span>
                  SSH Password (Standard password will be used)
                </div>
              </div>

              {/* Security Notice */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-4 w-4 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-xs font-medium text-yellow-800">Security Notice</p>
                    <p className="text-xs text-yellow-700">Your SSH password will be set during VPS creation. Store it securely!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => navigate('/vps#packages')}
        onAuthSuccess={handleAuthSuccess}
      />
      
      <PaymentVerificationModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onVerificationSuccess={handlePaymentAndProvisionSuccess}
        planId={selectedPlan?.id || ''}
        billingCycle={billingCycle}
        vpsName={vpsName.trim()}
        osTemplate={osTemplate}
        amount={totalPrice}
      />
    </div>
  );
};

export default StreamlinedVPSCheckout;